import React from "react";
import { Minus, Plus } from "lucide-react";
import { Input } from "../ui/input";

export const SO_TEAL = "#0d9488";
export const MAX_ADDRESS_ROWS = 20;

export function emptyAddressRows(count) {
  return Array.from({ length: Math.max(count, 0) }, () => "");
}

export function normalizeAddressRows(list, fallbacks = [], minRows = 1, maxRows = MAX_ADDRESS_ROWS) {
  const stored = Array.isArray(list)
    ? list.map((v) => v || "")
    : fallbacks.map((v) => v || "");
  const next = stored.length ? [...stored] : emptyAddressRows(minRows);
  while (next.length < minRows) next.push("");
  return next.slice(0, maxRows);
}

export function FieldRow({ label, children, className = "" }) {
  return (
    <div className={`flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 py-2 ${className}`}>
      <label className="text-sm font-medium shrink-0 sm:w-52 text-slate-700 dark:text-slate-300">
        {label}
      </label>
      <div className="flex-1">{children}</div>
    </div>
  );
}

export function UnderlineInput({ className = "", readOnly, accentColor = SO_TEAL, ...props }) {
  return (
    <Input
      className={`border-0 border-b-2 rounded-none px-1 shadow-none focus-visible:ring-0 bg-transparent ${readOnly ? "opacity-80 cursor-not-allowed" : ""} ${className}`}
      style={{ borderColor: accentColor }}
      readOnly={readOnly}
      {...props}
    />
  );
}

export function AddAddressButton({
  onClick,
  label,
  accentColor = SO_TEAL,
  testId,
  disabled = false,
}) {
  return (
    <div className="flex items-center gap-2 py-1">
      <div className="hidden sm:block sm:w-52 shrink-0" />
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className="inline-flex items-center justify-center h-8 w-8 rounded-full text-white shadow-sm hover:opacity-90 disabled:opacity-40"
        style={{ backgroundColor: accentColor }}
        aria-label={label}
        title={label}
        data-testid={testId}
      >
        <Plus size={16} />
      </button>
    </div>
  );
}

export function DynamicAddressList({
  values,
  onChange,
  labelTemplate,
  addLabel,
  removeLabel,
  readOnly = false,
  minRows = 1,
  maxRows = MAX_ADDRESS_ROWS,
  accentColor = SO_TEAL,
  testIdPrefix = "address",
}) {
  const list = values?.length ? values : emptyAddressRows(minRows);

  const setItem = (index, value) => {
    if (readOnly) return;
    const next = [...list];
    next[index] = value;
    onChange(next);
  };

  const addItem = () => {
    if (readOnly || list.length >= maxRows) return;
    onChange([...list, ""]);
  };

  const removeItem = (index) => {
    if (readOnly || list.length <= minRows) return;
    onChange(list.filter((_, i) => i !== index));
  };

  return (
    <>
      {list.map((addr, i) => (
        <FieldRow
          key={`${testIdPrefix}-${i}`}
          label={labelTemplate.replace("{n}", String(i + 1).padStart(2, "0"))}
        >
          <div className="flex items-center gap-2">
            <UnderlineInput
              className="flex-1"
              value={addr}
              onChange={(e) => setItem(i, e.target.value)}
              readOnly={readOnly}
              accentColor={accentColor}
            />
            {!readOnly && i >= minRows && (
              <button
                type="button"
                onClick={() => removeItem(i)}
                className="shrink-0 inline-flex items-center justify-center h-8 w-8 rounded-full text-slate-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40"
                aria-label={removeLabel}
                title={removeLabel}
                data-testid={`${testIdPrefix}-remove-${i}`}
              >
                <Minus size={16} />
              </button>
            )}
          </div>
        </FieldRow>
      ))}
      {!readOnly && list.length < maxRows && (
        <AddAddressButton
          onClick={addItem}
          label={addLabel}
          accentColor={accentColor}
          testId={`${testIdPrefix}-add`}
        />
      )}
    </>
  );
}
